import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FolderTree, GripVertical, ImagePlus, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { catalogAPI } from '../../lib/api';
import { t } from '../../lib/i18n';
import { Field, Modal, btnGhost, btnPrimary, inputCls } from '../../components/ui';
import ImageAdjust from '../../components/ImageAdjust';
import {
  Badge, EmptyPanel, IconButton, PageHeader, RowMenu, adminBtnPrimary, adminInput,
} from '../../components/adminUi';

// Admin 3 · Categories (s134 redesign): one card per top-level category with its
// sub-categories nested inside (+ Add sub-category); drag reorders siblings (the
// order drives the home pills); a "New category" form stays open on the right.

// SVGs aren't canvas-friendly — they upload raw (adjust modal skipped).
const isSvg = (file) => file && (file.type === 'image/svg+xml' || /\.svg$/i.test(file.name || ''));

const slugify = (s) => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
  .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

function CatThumb({ cat, size }) {
  if (cat.thumbnail) return <img src={cat.thumbnail} alt="" className="shrink-0 rounded-[10px] object-cover" style={{ width: size, height: size }} />;
  const initials = (cat.name || '?').split(/\s+/).filter((w) => /\w/.test(w)).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
  return (
    <span
      className="flex shrink-0 items-center justify-center rounded-[10px] bg-lms-current font-bold text-lms-sidebar"
      style={{ width: size, height: size, fontSize: size > 40 ? 13 : 11 }}
    >
      {initials}
    </span>
  );
}

function CountBadge({ n }) {
  return <Badge kind="neutral">{n} {n === 1 ? t('course') : t('courses')}</Badge>;
}

// Row body shared by parents and subs; the ⋮ menu carries image + delete.
function CatRow({ cat, size, handle, onEdit, onDelete, onUpload }) {
  const fileRef = useRef(null);
  const [adjustFile, setAdjustFile] = useState(null);
  return (
    <>
      {handle}
      <CatThumb cat={cat} size={size} />
      <div className="flex min-w-0 flex-1 flex-col">
        <span className={`truncate font-bold text-lms-ink ${size > 40 ? 'text-[15.5px]' : 'text-sm'}`}>{cat.name}</span>
        <span className="truncate text-[12.5px] text-lms-faint">/{cat.slug}</span>
      </div>
      <CountBadge n={cat.course_count || 0} />
      <IconButton label={t('Edit')} onClick={() => onEdit(cat)}><Pencil size={14} /></IconButton>
      <RowMenu
        items={[
          { label: cat.thumbnail ? t('Change image') : t('Add image'), icon: ImagePlus, onClick: () => fileRef.current && fileRef.current.click() },
          { label: t('Delete'), icon: Trash2, onClick: () => onDelete(cat), danger: true },
        ]}
      />
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => { const f = e.target.files[0]; if (f) { if (isSvg(f)) onUpload(cat, f); else setAdjustFile(f); } e.target.value = ''; }}
      />
      {adjustFile && (
        <ImageAdjust file={adjustFile} targetRatio={1} onCancel={() => setAdjustFile(null)}
          onConfirm={(blobOrFile, filename) => { setAdjustFile(null); const payload = (blobOrFile instanceof File) ? blobOrFile : new File([blobOrFile], filename, { type: blobOrFile.type }); onUpload(cat, payload); }} />
      )}
    </>
  );
}

function useDragHandle(id) {
  const s = useSortable({ id });
  const handle = (
    <button type="button" {...s.attributes} {...s.listeners} className="cursor-grab touch-none text-lms-faint hover:text-lms-ink" aria-label={t('Drag to reorder')}>
      <GripVertical size={15} />
    </button>
  );
  const style = { transform: CSS.Transform.toString(s.transform), transition: s.transition, zIndex: s.isDragging ? 10 : undefined };
  return { ref: s.setNodeRef, style, handle, dragging: s.isDragging };
}

function SubRow({ cat, ...rest }) {
  const d = useDragHandle(cat.id);
  return (
    <div ref={d.ref} style={d.style} className={`flex items-center gap-3 border-t border-lms-line bg-lms-surface py-3 pl-11 pr-4 ${d.dragging ? 'shadow-[var(--lms-elev-2)]' : ''}`}>
      <span aria-hidden="true" className="-mt-3.5 h-5 w-[18px] shrink-0 rounded-bl-md border-b-2 border-l-2 border-lms-line" />
      <CatRow cat={cat} size={36} handle={d.handle} {...rest} />
    </div>
  );
}

function ParentCard({ cat, subs, sensors, onSubDragEnd, onAddSub, ...rest }) {
  const d = useDragHandle(cat.id);
  return (
    <section ref={d.ref} style={d.style} className={`overflow-hidden rounded-xl border border-lms-line bg-lms-surface ${d.dragging ? 'shadow-[var(--lms-elev-3)]' : ''}`}>
      <div className="flex items-center gap-3.5 px-4 py-3.5">
        <CatRow cat={cat} size={48} handle={d.handle} {...rest} />
      </div>
      {subs.length > 0 && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onSubDragEnd}>
          <SortableContext items={subs.map((s) => s.id)} strategy={verticalListSortingStrategy}>
            {subs.map((s) => <SubRow key={s.id} cat={s} {...rest} />)}
          </SortableContext>
        </DndContext>
      )}
      <div className="border-t border-lms-line py-2.5 pl-[72px] pr-4">
        <button
          type="button"
          onClick={() => onAddSub(cat)}
          className="lms-press inline-flex h-9 items-center gap-1.5 rounded-lg border border-dashed border-lms-pill bg-lms-surface px-3 text-[13px] font-bold text-lms-ink hover:border-lms-ink"
        >
          <Plus size={14} /> {t('Add sub-category')}
        </button>
      </div>
    </section>
  );
}

export default function CategoriesPage() {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // category being edited (modal)
  const [editName, setEditName] = useState('');
  const [editParent, setEditParent] = useState('');
  const [newName, setNewName] = useState('');
  const [newParent, setNewParent] = useState('');
  const [newFile, setNewFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const nameRef = useRef(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const load = useCallback(async () => {
    try {
      const { data } = await catalogAPI.list();
      setCats(Array.isArray(data) ? data : data.categories || []);
    } catch {
      toast.error(t('Could not load categories'));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const byOrder = (a, b) => (a.order || 0) - (b.order || 0);
  const tops = useMemo(() => cats.filter((c) => !c.parent_id).sort(byOrder), [cats]);
  const childrenOf = useCallback((id) => cats.filter((c) => c.parent_id === id).sort(byOrder), [cats]);

  // Reorder within one sibling group (top level, or one parent's subs).
  const reorder = async (siblings, activeId, overId) => {
    const ids = siblings.map((c) => c.id);
    const moved = arrayMove(ids, ids.indexOf(activeId), ids.indexOf(overId));
    const orderMap = Object.fromEntries(moved.map((id, i) => [id, i]));
    setCats((prev) => prev.map((c) => (c.id in orderMap ? { ...c, order: orderMap[c.id] } : c)));
    try {
      await catalogAPI.reorder(moved);
    } catch {
      toast.error(t('Reorder failed'));
      load();
    }
  };
  const onTopDragEnd = ({ active, over }) => {
    if (over && active.id !== over.id) reorder(tops, active.id, over.id);
  };
  const onSubDragEnd = (parentId) => ({ active, over }) => {
    if (over && active.id !== over.id) reorder(childrenOf(parentId), active.id, over.id);
  };

  const create = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setBusy(true);
    try {
      const { data } = await catalogAPI.create({ name: newName.trim(), parent_id: newParent || null });
      if (newFile && data && data.id) {
        try {
          await catalogAPI.upload(data.id, newFile);
        } catch {
          toast.error(t('Category created, but the image upload failed'));
        }
      }
      toast.success(t('Category created'));
      setNewName('');
      setNewParent('');
      setNewFile(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Save failed'));
    } finally {
      setBusy(false);
    }
  };

  const openEdit = (cat) => {
    setEditing(cat);
    setEditName(cat.name);
    setEditParent(cat.parent_id || '');
  };
  const saveEdit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await catalogAPI.update(editing.id, { name: editName, parent_id: editParent || null });
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Save failed'));
    } finally {
      setBusy(false);
    }
  };

  const del = async (cat) => {
    if (!window.confirm(`${t('Delete')} "${cat.name}"?`)) return;
    try {
      await catalogAPI.remove(cat.id);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Delete failed'));
    }
  };

  const upload = async (cat, file) => {
    try {
      await catalogAPI.upload(cat.id, file);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Upload failed'));
    }
  };

  const addSub = (parent) => {
    setNewParent(parent.id);
    if (nameRef.current) nameRef.current.focus();
  };

  const subCount = cats.filter((c) => c.parent_id).length;
  const rowProps = { onEdit: openEdit, onDelete: del, onUpload: upload };

  return (
    <div className="flex flex-col gap-[22px]">
      <PageHeader
        title={t('Categories')}
        subtitle={`${tops.length} ${tops.length === 1 ? t('category') : t('categories')} · ${subCount} ${subCount === 1 ? t('sub-category') : t('sub-categories')} · ${t('drag to set the order of the pills on the home.')}`}
      />

      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr),340px]">
        <div className="flex min-w-0 flex-col gap-3.5">
          {loading ? (
            <p className="text-sm text-lms-muted">{t('Loading…')}</p>
          ) : tops.length === 0 ? (
            <EmptyPanel icon={FolderTree} title={t('No categories yet')}>
              {t('Create the first one with the form on the right.')}
            </EmptyPanel>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onTopDragEnd}>
              <SortableContext items={tops.map((c) => c.id)} strategy={verticalListSortingStrategy}>
                {tops.map((c) => (
                  <ParentCard
                    key={c.id}
                    cat={c}
                    subs={childrenOf(c.id)}
                    sensors={sensors}
                    onSubDragEnd={onSubDragEnd(c.id)}
                    onAddSub={addSub}
                    {...rowProps}
                  />
                ))}
              </SortableContext>
            </DndContext>
          )}
        </div>

        <form onSubmit={create} className="flex flex-col gap-4 rounded-xl border border-lms-line bg-lms-surface p-5 sm:px-6 lg:sticky lg:top-[84px]">
          <h2 className="text-base font-bold text-lms-ink">{t('New category')}</h2>
          <label className="flex flex-col gap-1.5">
            <span className="text-[13px] font-semibold text-lms-body">{t('Name')}</span>
            <input ref={nameRef} value={newName} onChange={(e) => setNewName(e.target.value)} placeholder={t('e.g. Real Estate')} className={adminInput} />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-[13px] font-semibold text-lms-body">{t('Parent')}</span>
            <select value={newParent} onChange={(e) => setNewParent(e.target.value)} className={adminInput}>
              <option value="">{t('None (top level)')}</option>
              {tops.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <span className="text-xs text-lms-faint">{t('Pick a parent to make it a sub-category.')}</span>
          </label>
          <div className="flex flex-col gap-1.5">
            <span className="text-[13px] font-semibold text-lms-body">{t('URL')}</span>
            <div className={`${adminInput} flex items-center truncate text-lms-faint`}>
              /courses?category=<span className="truncate text-lms-ink">{slugify(newName) || '…'}</span>
            </div>
            <span className="text-xs text-lms-faint">{t('Generated from the name.')}</span>
          </div>
          <label className="flex h-24 cursor-pointer items-center justify-center gap-2.5 rounded-[10px] border-[1.5px] border-dashed border-lms-pill px-3 text-center text-[13.5px] text-lms-sub hover:border-lms-ink">
            <ImagePlus size={20} />
            {newFile ? newFile.name : t('Upload a square image')}
            <input type="file" accept="image/*" className="hidden" onChange={(e) => { setNewFile(e.target.files[0] || null); e.target.value = ''; }} />
          </label>
          <button type="submit" disabled={busy || !newName.trim()} className={adminBtnPrimary}>
            {busy ? t('Saving…') : t('Create category')}
          </button>
        </form>
      </div>

      {editing && (
        <Modal title={t('Edit category')} onClose={() => setEditing(null)}>
          <form onSubmit={saveEdit} className="space-y-4">
            <Field label={t('Name')}>
              <input value={editName} onChange={(e) => setEditName(e.target.value)} required autoFocus className={inputCls} />
            </Field>
            <Field label={t('Parent (optional — makes it a sub-category)')}>
              <select value={editParent} onChange={(e) => setEditParent(e.target.value)} className={inputCls}>
                <option value="">{t('None (top level)')}</option>
                {tops.filter((c) => c.id !== editing.id).map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </Field>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setEditing(null)} className={btnGhost}>{t('Cancel')}</button>
              <button type="submit" disabled={busy} className={btnPrimary}>
                {busy ? t('Saving…') : t('Save')}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
